/* client-api: idempotente mutaties + outbox voor offline (dependency-chain). */
import { outboxAdd, outboxFlush, outboxCount, type OutboxJob } from "./outbox";
import { privateEpoch, requirePrivateEpoch } from "./privacyEpoch";

export const API = "/api/v1";
const ik = () => crypto.randomUUID();

const READ_TIMEOUT_MS = 15_000;
const WRITE_TIMEOUT_MS = 30_000;

class RequestTimeoutError extends Error {
  constructor() { super("request timed out"); this.name = "RequestTimeoutError"; }
}

async function fetchBounded(input: RequestInfo | URL, init: RequestInit,
  timeoutMs: number): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (e) {
    if (controller.signal.aborted) throw new RequestTimeoutError();
    throw e;
  } finally {
    clearTimeout(timer);
  }
}

export class ApiError extends Error {
  constructor(public status: number, public title: string, public detail?: string) {
    super(title);
    /* ALLEEN 401 betekent «deze sessie geldt niet meer». Dat hier afhandelen
       in plaats van bij één aanroeper voorkomt dat de app blijft draaien
       omdat toevallig een ándere call als eerste faalde.

       Een 403 is iets heel anders: je bent ingelogd, maar mag dít ene ding
       niet -- geen grant op andermans media, een capsule die je zelf niet mag
       openen. Die hoort een zichtbare, lokale fout te blijven. Ze op één hoop
       gooien wierp mensen eruit op een volkomen normale weigering. */
    if (status === 401) void notifyRevoked();
  }
}

/* Lazy geïmporteerd: session.ts mag api.ts niet terugimporteren. */
async function notifyRevoked(): Promise<void> {
  try {
    const { revokeSession } = await import("./session");
    revokeSession();
  } catch { /* module weg: dan valt er niets te melden */ }
}

export async function get<T>(path: string): Promise<T> {
  let r: Response;
  try {
    r = await fetchBounded(API + path, { credentials: "same-origin" }, READ_TIMEOUT_MS);
  } catch (e) {
    if (e instanceof RequestTimeoutError)
      throw new ApiError(504, "The request took too long", "Please try again.");
    throw e;
  }
  if (r.status === 401) {
    /* Sessie weg. De intrekking is gemeld (zie ApiError) en de app sluit af.
       Deze aanvraag krijgt bewust géén antwoord meer: hem laten afwijzen zou
       in elke fire-and-forget `void get(...)` een onafgevangen rejection
       opleveren -- een runtimefout voor iets wat we juist netjes afhandelen.
       Er valt hierna niets meer te doen; de boom wordt gedemonteerd. */
    void new ApiError(401, r.statusText);
    return new Promise<T>(() => { /* nooit */ });
  }
  if (!r.ok) throw new ApiError(r.status, r.statusText);
  return r.json();
}

/** online mutatie; bij netwerkfout → outbox (met optionele afhankelijkheid) */
export async function mutate<T>(method: string, path: string, body?: unknown,
  opts?: { ifMatch?: number; offline?: { deps?: string[]; blob?: Blob; kind?: string };
           key?: string; queueOnNetworkError?: boolean }):
  Promise<T | { queued: true; mutationId: string }> {
  const key = opts?.key ?? ik(), epoch = privateEpoch();
  requirePrivateEpoch(epoch);
  try {
    const r = await fetchBounded(API + path, {
      method,
      credentials: "same-origin",
      headers: {
        "content-type": "application/json",
        "idempotency-key": key,
        ...(opts?.ifMatch !== undefined ? { "if-match": String(opts.ifMatch) } : {}),
      },
      body: body === undefined ? null : JSON.stringify(body),
    }, WRITE_TIMEOUT_MS);
    if (!r.ok) {
      const p = await r.json().catch(() => ({ title: r.statusText }));
      throw new ApiError(r.status, p.title ?? r.statusText, p.detail);
    }
    const result = await r.json();
    requirePrivateEpoch(epoch);
    return result;
  } catch (e) {
    requirePrivateEpoch(epoch);
    if (e instanceof ApiError) throw e;                    // HTTP-fout: nooit stil queuen
    /* Vluchtige live-acties (Огонёк, Same Sky) hebben later geen betekenis.
       Ze alsnog uit de outbox versturen zou een tik van uren geleden als een
       nieuw signaal presenteren. Die aanroepers kiezen daarom expliciet voor
       een zichtbare netwerkfout in plaats van een wachtrij-item. */
    if (opts?.queueOnNetworkError === false) throw e;
    const job: OutboxJob = {
      mutationId: key, method, path, body, deps: opts?.offline?.deps ?? [],
      attempts: 0, status: "pending", createdAt: Date.now(),
      blobKind: opts?.offline?.kind ?? null,
    };
    await outboxAdd(job, opts?.offline?.blob ?? null, epoch);
    return { queued: true, mutationId: key };
  }
}

export async function uploadMedia(blob: Blob, name: string):
  Promise<{ id: string } | { queued: true; mutationId: string }> {
  const key = ik(), epoch = privateEpoch();
  requirePrivateEpoch(epoch);
  try {
    const fd = new FormData();
    fd.append("file", blob, name);
    const r = await fetchBounded(API + "/media", {
      method: "POST", credentials: "same-origin", body: fd,
      headers: { "idempotency-key": key },
    }, WRITE_TIMEOUT_MS);
    if (!r.ok) {
      const p = await r.json().catch(() => ({ title: r.statusText }));
      throw new ApiError(r.status, p.title ?? r.statusText);
    }
    const result = await r.json();
    requirePrivateEpoch(epoch);
    return result;
  } catch (e) {
    requirePrivateEpoch(epoch);
    if (e instanceof ApiError) throw e;
    const job: OutboxJob = {
      mutationId: key, method: "POST", path: "/media", body: null, deps: [],
      attempts: 0, status: "pending", createdAt: Date.now(), blobKind: "media",
    };
    await outboxAdd(job, blob, epoch);
    return { queued: true, mutationId: key };
  }
}

export { outboxFlush, outboxCount };
export const mediaUrl = (id: string, variant?: "thumb") =>
  `${API}/media/${id}?privacy=2${variant ? `&variant=${variant}` : ""}`;
